import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormSpy, useForm, useFormState } from 'react-final-form';
import StripePaymentButton from '../PaymentButtons/StripePaymentButton';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { DEFAULT_CURRENCY, formatAmount } from '../../../../../../utils/currency';
import { filterActiveMethods, PAYMENT_MODE } from '../../../../../../utils/payments';
import { DEFAULT_COUNTRY_CODE, RG, SD } from '../../../../../../utils/constants';
import { getStripePublishableKey } from '../../../../../../utils/payments';
import CreditCardForm from '../CreditCardForm';
import BankAccountForm from '../BankAccountForm';
import Button from '../../../../../01_atoms/Button';
import CreditCardIcon from '../../../../../../public/static/icons/credit-card.svg';
import BankAccountIcon from '../../../../../../public/static/icons/direct-debit.svg';
import { scrollToElement } from '../../../../../../utils/scroll-to';
import PayPalPaymentButton from '../PaymentButtons/PayPalPaymentButton';
import PaymentModeWarning from '../../../../../01_atoms/PaymentModeWarning';
import SubmissionError from '../../../../../01_atoms/DonationFormAlert/SubmissionError';
import PayPalForm from '../PayPalForm';
import RevolutForm from '../RevolutForm';
import StripeForm from '../StripeForm';
import PayPalIcon from '../../../../../../public/static/icons/payment-methods/paypal.svg';
import RevolutIcon from '../../../../../../public/static/icons/payment-methods/revolut.svg';
import ApplePayIcon from '../../../../../../public/static/icons/payment-methods/apple-pay.svg';
import GooglePayIcon from '../../../../../../public/static/icons/payment-methods/google-pay.svg';
import { pushDonationFormValidationErrors as gtmPushDonationFormValidationErrors } from '../../../../../../utils/gtm';
import { getFundraisingCosts } from '../../../../../../utils/donationForm';

import styles from './index.module.scss';

const stripePromise = loadStripe(getStripePublishableKey());

const Payments = ({
  error = '',
  orderId = null,
  reCaptcha = null,
  onError,
  onSuccessPayment,
  setProcessing,
  submitFormWithValidationErrors,
  proceedWithGiftAidPopup,
  style,
  showFundraisingCosts = false,
  fundraisingCostsTooltip = '',
  fundraisingCostsInitValue = false,
  showRevolutStripe = false,
}) => {
  const formState = useFormState();
  const { values } = formState;
  const form = useForm();
  const [stripeButtonAvailability, setStripeButtonAvailability] = useState(null);
  const [stripeButtonAvailabilityChecked, setStripeButtonAvailabilityChecked] = useState(false);
  const [openedForm, setOpenedForm] = useState('');
  const [payPalDisabled, setPayPalDisabled] = useState(true);

  // Filter payment methods that we need to display.
  const paymentMethods = {};
  filterActiveMethods({
    mode: PAYMENT_MODE,
    donationType: values.donation_type === 'single' ? SD : RG,
    region: DEFAULT_COUNTRY_CODE,
    stripeButtonAvailability,
    showRevolutStripe,
  }).forEach((item) => {
    if (item.id.startsWith('decoupled_stripe_button_apple_pay')) {
      paymentMethods['apple_pay'] = item;
    } else if (item.id.startsWith('decoupled_stripe_button_google_pay')) {
      paymentMethods['google_pay'] = item;
    } else if (item.id.startsWith('decoupled_stripe_credit_card')) {
      paymentMethods['credit_card'] = item;
    } else if (item.id.startsWith('paypal')) {
      paymentMethods['paypal'] = item;
    } else if (item.id.startsWith('decoupled_stripe_revolut')) {
      paymentMethods['revolut'] = item;
    } else if (item.id.startsWith('direct_debit')) {
      paymentMethods['direct_debit'] = item;
    }
  });

  const resetFundraisingCosts = () => {
    form.mutators.setValue('fundraisingCostsApplied', false);
    form.mutators.setValue('fundraising_costs', []);
  };

  const setFundraisingCostsInitialValue = () => {
    if (fundraisingCostsInitValue) {
      form.mutators.setValue('fundraisingCostsApplied', true);
      form.mutators.setValue('fundraising_costs', ['fundraising_costs']);
    } else {
      resetFundraisingCosts();
    }
  };

  const recalculateFundraisingCosts = (values) => {
    // Recalculating fundraising costs and total amount.
    let formula = 'stripe';
    if (openedForm === 'paypal') {
      formula = 'paypal';
    } else if (openedForm === 'revolut') {
      formula = 'revolut';
    }

    const fundraisingCostsAmount = getFundraisingCosts(values.amount.single, formula);
    form.mutators.setValue('fundraisingCostsAmount', fundraisingCostsAmount);
    form.mutators.setValue(
      'totalAmount',
      Number(values.amount.single) + Number(fundraisingCostsAmount),
    );
  };

  const updateFundraisingCosts = ({ values }) => {
    // Reset fundraising cost state on donation type change.
    if (values.donation_type !== values.previousDonationType) {
      form.mutators.setValue('previousDonationType', values.donation_type);
      if (values.donation_type === 'single') {
        setFundraisingCostsInitialValue();
      } else {
        resetFundraisingCosts();
      }
    }

    // Recalculating fundraising costs and total amount on amount change.
    if (values.amount.single !== values.previousAmount) {
      recalculateFundraisingCosts(values);
      form.mutators.setValue('previousAmount', values.amount.single);
    }

    // Updating state on fundraising costs checkbox change.
    if (values.fundraising_costs && values.fundraising_costs[0] === 'fundraising_costs') {
      if (!values.fundraisingCostsApplied) {
        form.mutators.setValue('fundraisingCostsApplied', true);
      }
    } else {
      if (values.fundraisingCostsApplied) {
        form.mutators.setValue('fundraisingCostsApplied', false);
      }
    }
  };

  useEffect(() => {
    // Recalculate fundraising costs and total amount on payment method change.
    recalculateFundraisingCosts(values);

    // Reset fundraising costs checkbox state.
    if (['credit-card', 'revolut', 'apple-pay', 'google-pay', 'paypal'].includes(openedForm)) {
      setFundraisingCostsInitialValue();
    } else {
      resetFundraisingCosts();
    }

    // If CC fields were filled and Donation was submitted with another payment type,
    // and when the donation was failed/canceled, we should reset CC fields to re-validate them
    // on the next submit.
    if (openedForm === 'credit-card') {
      form.mutators.setValue('payment_options', {});
    }
  }, [openedForm]);

  // Defined payment method for StripePaymentButton.
  const stripePaymentMethod = paymentMethods['apple_pay']
    ? paymentMethods['apple_pay']
    : paymentMethods['google_pay']
      ? paymentMethods['google_pay']
      : '';

  const StripeButton = (showButton) => (
    <StripePaymentButton
      // Pass order id to the StripePaymentButton to finalise created order.
      orderId={
        stripePaymentMethod && stripePaymentMethod.id === values.payment_method_id && orderId
          ? orderId
          : null
      }
      // We use random paymentMethodId just to check if Apple/Google pay available.
      paymentMethodId={stripePaymentMethod ? stripePaymentMethod.id : 'initialise'}
      showButton={showButton}
      captchaToken={values.captcha_token}
      captchaObject={reCaptcha}
      onStripeButtonAvailabilityCheck={(result) => {
        setStripeButtonAvailabilityChecked(true);
        setStripeButtonAvailability(result);
      }}
      beforePaymentMethod={() => setProcessing(true)}
      onSuccessPayment={onSuccessPayment}
      onStripeButtonClick={(paymentMethodId, proceedWithPayment) => {
        form.mutators.setValue('payment_method_id', paymentMethodId);

        // Show validation errors if form isn't valid.
        if (
          !(
            formState.valid ||
            (Object.keys(formState.errors).length === 1 && formState.errors['payment_options'])
          )
        ) {
          form.submit();
          return;
        }

        // Gift Aid popup for the UK.
        if (DEFAULT_COUNTRY_CODE === 'GB') {
          // Save proceedWithPayment function and show GiftAid popup.
          proceedWithGiftAidPopup(proceedWithPayment);
          return;
        }

        proceedWithPayment();
      }}
      // Remove error and unlock form.
      onCancelPayment={() => onError('')}
      onError={onError}
    />
  );

  const StripeInitComponent = StripeButton(false);
  const StripeButtonComponent = StripeButton(true);

  // Subscribe PayPal button to validation process to enable it
  // in case when no validation errors found for the form.
  useEffect(() => {
    form.subscribe(
      (result) => {
        // Ignore validation errors for hidden forms if they were opened.
        if (
          result.valid ||
          (result.errors &&
            result.errors['payment_options'] &&
            Object.keys(result.errors).length === 1)
        ) {
          setPayPalDisabled(false);
        } else {
          setPayPalDisabled(true);
        }
      },
      { valid: true, errors: true },
    );
  }, [form]);

  const PayPalButtonComponent = (
    <div className={styles['payments__button-with-icon']}>
      <PayPalPaymentButton
        donationType={values.donation_type === 'monthly' ? RG : SD}
        onError={onError}
        onSuccessPayment={onSuccessPayment}
        paymentMethodId={paymentMethods.paypal.id}
        disabled={payPalDisabled}
        // Unlock form.
        onCancel={() => onError('')}
        // PayPal component expects to receive Promise, therefore
        // we explicitly pass Promise to it. We can't use form.submit()
        // because the function contains validation on top,
        // and can return not Promise element. PayPal button
        // will be disabled in case of validation errors
        // in other fields. See 'validate' property.
        //
        // Also, if the form has been previously submitted with
        // different payment method and contains validation error
        // in payment details, and after that user just click on
        // PayPal button, sometimes submission was processed before
        // payment method is actually changed in form state.
        // Passing it explicitly to avoid any inconsistency.
        onBeforeAuthentication={() =>
          submitFormWithValidationErrors({
            ...values,
            payment_method_id: paymentMethods.paypal.id,
          })
        }
        onClick={() => {
          // Set correct payment gateway.
          form.mutators.setValue('payment_method_id', paymentMethods.paypal.id);

          // Show validation errors.
          // Ignore validation errors for hidden forms if they were opened.
          const isPaymentOptionsError =
            Object.keys(formState.errors).length === 1 && formState.errors['payment_options'];
          if (formState.errors && Object.keys(formState.errors).length && !isPaymentOptionsError) {
            form.submit();
          }

          // Push the errors to gtm.
          if (formState.errors && formState.hasValidationErrors) {
            gtmPushDonationFormValidationErrors(
              `payment.${paymentMethods.paypal.id}`,
              formState.errors,
            );
          }
        }}
      />
    </div>
  );

  const amount = +values.amount[values.donation_type];

  return (
    <Elements
      stripe={stripePromise}
      options={{
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans',
          },
        ],
      }}
    >
      <FormSpy subscription={{ values: true }} onChange={updateFundraisingCosts} />
      <div className={styles['payments']}>
        {error && (
          <SubmissionError
            {...error}
            onCloseClick={() => onError('')}
            className={styles['payments__error']}
          />
        )}
        <div className={styles['payments__title']}>
          Your donation <PaymentModeWarning />
        </div>
        <div className={styles['payments__description']}>
          {amount > 0 && (
            <span>
              You’re about to make a{' '}
              <strong>{values.donation_type === 'monthly' ? 'monthly' : 'one-off'}</strong> donation
              of:
              <strong className={styles['payments__description-amount']}>
                {formatAmount(DEFAULT_CURRENCY, amount)}
              </strong>
            </span>
          )}
        </div>

        {/** Initialise StripePaymentButton during the first render
         to get information about availability ApplePay or GooglePay */}
        {(!stripeButtonAvailabilityChecked || stripePaymentMethod) && StripeInitComponent}

        <div className={styles['payments__buttons-container']}>
          {paymentMethods.apple_pay && (
            <Button
              data-component="inline-donation-form-apple-pay-open-form"
              className={`${styles['payments__button-with-icon']} ${styles['payments__button-with-icon--apple-pay']}`}
              tag="a"
              onClick={() => {
                setOpenedForm('apple-pay');
                scrollToElement({ elementId: 'bb-inline-donation-form-payment-form' });
              }}
            >
              <ApplePayIcon />
            </Button>
          )}
          {paymentMethods.google_pay && (
            <Button
              data-component="inline-donation-form-google-pay-open-form"
              className={`${styles['payments__button-with-icon']} ${styles['payments__button-with-icon--google-pay']}`}
              tag="a"
              onClick={() => {
                setOpenedForm('google-pay');
                scrollToElement({ elementId: 'bb-inline-donation-form-payment-form' });
              }}
            >
              <GooglePayIcon />
            </Button>
          )}

          {paymentMethods.paypal && (
            <Button
              data-component="inline-donation-form-paypal-open-form"
              className={`${styles['payments__button-with-icon']} ${styles['payments__button-with-icon--paypal']}`}
              tag="a"
              onClick={() => {
                setOpenedForm('paypal');
                scrollToElement({ elementId: 'bb-inline-donation-form-payment-form' });
              }}
            >
              <PayPalIcon />
            </Button>
          )}

          {paymentMethods.revolut && (
            <Button
              data-component="inline-donation-form-revolut-open-form"
              className={`${styles['payments__button-with-icon']} ${styles['payments__button-with-icon--revolut']}`}
              tag="a"
              onClick={() => {
                // Fundraising costs checkbox state for Revolut is handled in useEffect.
                setOpenedForm('revolut');
                scrollToElement({ elementId: 'bb-inline-donation-form-payment-form' });
              }}
            >
              <RevolutIcon />
            </Button>
          )}
        </div>

        <div className={styles['payments__buttons-separator-container']}>
          {['apple_pay', 'google_pay', 'paypal', 'revolut'].some(
            (method) => method in paymentMethods,
          ) && (
            <div className={styles['payments__separator']}>
              <div className={styles['payments__separator_line']} />
              <div className={styles['payments__separator_content']}>OR</div>
            </div>
          )}
          <div className={styles['payments__buttons-container']}>
            {paymentMethods.credit_card && (
              <Button
                data-component="inline-donation-form-credit-card-open-form"
                className={`${styles['payments__button-with-icon']} ${styles['payments__button-with-icon--default']}`}
                tag="a"
                type={style === 'emergency' ? 'activism-black' : 'secondary'}
                onClick={() => {
                  // Fundraising costs checkbox state for credit card is handled in useEffect.
                  setOpenedForm('credit-card');
                  scrollToElement({ elementId: 'bb-inline-donation-form-payment-form' });
                }}
              >
                <CreditCardIcon /> Debit or Credit Card
              </Button>
            )}

            {paymentMethods.direct_debit && (
              <Button
                data-component="inline-donation-form-bank-account-open-form"
                className={`${styles['payments__button-with-icon']} ${styles['payments__button-with-icon--default']}`}
                tag="a"
                type={style === 'emergency' ? 'activism-black' : 'secondary'}
                onClick={() => {
                  setOpenedForm('bank-account');
                  resetFundraisingCosts();
                  // Set default values for bank account form.
                  if (paymentMethods.direct_debit.defaults) {
                    let paymentOptions = values.payment_options;
                    if (!paymentOptions) {
                      paymentOptions = {};
                    }
                    let setPaymentOptions = false;
                    Object.keys(paymentMethods.direct_debit.defaults).forEach((key) => {
                      if (!paymentOptions[key]) {
                        paymentOptions[key] = paymentMethods.direct_debit.defaults[key];
                        setPaymentOptions = true;
                      }
                    });

                    if (setPaymentOptions) {
                      form.mutators.setValue('payment_options', paymentOptions);
                    }
                  }
                  scrollToElement({ elementId: 'bb-inline-donation-form-payment-form' });
                }}
              >
                <BankAccountIcon /> Bank account
              </Button>
            )}
          </div>
        </div>
      </div>
      <div id="bb-inline-donation-form-payment-form">
        {openedForm === 'credit-card' && paymentMethods.credit_card && (
          <CreditCardForm
            paymentMethodId={paymentMethods['credit_card'] ? paymentMethods['credit_card'].id : ''}
            // Pass order id to the CreditCardForm to finalise created order.
            orderId={
              paymentMethods.credit_card.id === values.payment_method_id && orderId ? orderId : null
            }
            captchaToken={values.captcha_token}
            captchaObject={reCaptcha}
            onError={onError}
            onSuccessPayment={onSuccessPayment}
            onSubmitForm={() => {
              // Setup correct payment gateway when form submitted.
              form.mutators.setValue(
                'payment_method_id',
                paymentMethods['credit_card'] ? paymentMethods['credit_card'].id : '',
              );

              // Push the errors to gtm.
              if (formState.errors && formState.hasValidationErrors) {
                gtmPushDonationFormValidationErrors(
                  `payment.${formState.values.payment_method_id}`,
                  formState.errors,
                );
              }
            }}
            buttonType={style === 'emergency' ? 'emergency' : 'primary'}
            showFundraisingCosts={showFundraisingCosts}
            fundraisingCostsTooltip={fundraisingCostsTooltip}
          />
        )}
        {openedForm === 'google-pay' && paymentMethods.google_pay && (
          <StripeForm
            showFundraisingCosts={showFundraisingCosts}
            fundraisingCostsTooltip={fundraisingCostsTooltip}
            title={'Google Pay'}
            paymentButton={StripeButtonComponent}
          />
        )}
        {openedForm === 'apple-pay' && paymentMethods.apple_pay && (
          <StripeForm
            showFundraisingCosts={showFundraisingCosts}
            fundraisingCostsTooltip={fundraisingCostsTooltip}
            title={'Apple Pay'}
            paymentButton={StripeButtonComponent}
          />
        )}
        {openedForm === 'bank-account' && paymentMethods.direct_debit && (
          <BankAccountForm
            onSubmitForm={() => {
              // Setup correct payment gateway when form submitted.
              form.mutators.setValue(
                'payment_method_id',
                paymentMethods['direct_debit'] ? paymentMethods['direct_debit'].id : '',
              );

              // Push the errors to gtm.
              if (formState.errors && formState.hasValidationErrors) {
                gtmPushDonationFormValidationErrors(
                  `payment.${formState.values.payment_method_id}`,
                  formState.errors,
                );
              }
            }}
            buttonType={style === 'emergency' ? 'emergency' : 'primary'}
          />
        )}
        {openedForm === 'paypal' && paymentMethods.paypal && (
          <PayPalForm
            paypalButton={PayPalButtonComponent}
            showFundraisingCosts={showFundraisingCosts}
            fundraisingCostsTooltip={fundraisingCostsTooltip}
          />
        )}
        {openedForm === 'revolut' && paymentMethods.revolut && (
          <RevolutForm
            paymentMethodId={paymentMethods['revolut'] ? paymentMethods['revolut'].id : ''}
            // Pass order id to the RevolutForm to finalise created order.
            orderId={
              paymentMethods.revolut.id === values.payment_method_id && orderId ? orderId : null
            }
            onError={onError}
            onSubmitForm={() => {
              // Setup correct payment gateway when form submitted.
              form.mutators.setValue(
                'payment_method_id',
                paymentMethods['revolut'] ? paymentMethods['revolut'].id : '',
              );

              // Push the errors to gtm.
              if (formState.errors && formState.hasValidationErrors) {
                gtmPushDonationFormValidationErrors(
                  `payment.${formState.values.payment_method_id}`,
                  formState.errors,
                );
              }
            }}
            buttonType={style === 'emergency' ? 'emergency' : 'primary'}
            showFundraisingCosts={showFundraisingCosts}
            fundraisingCostsTooltip={fundraisingCostsTooltip}
          />
        )}
      </div>
    </Elements>
  );
};

Payments.propTypes = {
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  orderId: PropTypes.number,
  reCaptcha: PropTypes.object,
  onError: PropTypes.func.isRequired,
  onSuccessPayment: PropTypes.func.isRequired,
  setProcessing: PropTypes.func.isRequired,
  submitFormWithValidationErrors: PropTypes.func.isRequired,
  proceedWithGiftAidPopup: PropTypes.func.isRequired,
  style: PropTypes.oneOf(['default', 'emergency']).isRequired,
  showFundraisingCosts: PropTypes.bool,
  fundraisingCostsTooltip: PropTypes.string,
  fundraisingCostsInitValue: PropTypes.bool,
  showRevolutStripe: PropTypes.bool,
};

export default Payments;
